/* ============================================
   NODUS HOME · HERO
   Scroll-snap slideshow with dots, arrows and optional autoplay.
   ============================================ */

if (!customElements.get('nodus-home-hero')) {
	class NodusHomeHero extends HTMLElement {
		connectedCallback() {
			this.track = this.querySelector('[data-nodus-hero-track]');

			if (!this.track) return;

			this.dotsHolder = this.querySelector('[data-nodus-hero-dots]');
			this.prevButton = this.querySelector('[data-nodus-hero-prev]');
			this.nextButton = this.querySelector('[data-nodus-hero-next]');

			this.index = 0;
			this.timer = null;
			this.scrollFrame = null;
			this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

			this.handlePrev = () => this.goTo(this.index - 1, true);
			this.handleNext = () => this.goTo(this.index + 1, true);
			this.handleDotClick = this.onDotClick.bind(this);
			this.handleScroll = this.onScroll.bind(this);
			this.handleResize = this.onResize.bind(this);
			this.handlePause = () => this.stopAutoplay();
			this.handleResume = () => this.startAutoplay();

			if (this.prevButton) this.prevButton.addEventListener('click', this.handlePrev);

			if (this.nextButton) this.nextButton.addEventListener('click', this.handleNext);

			if (this.dotsHolder) this.dotsHolder.addEventListener('click', this.handleDotClick);

			this.track.addEventListener('scroll', this.handleScroll, { passive: true });
			window.addEventListener('resize', this.handleResize);
			this.addEventListener('mouseenter', this.handlePause);
			this.addEventListener('mouseleave', this.handleResume);
			this.addEventListener('focusin', this.handlePause);
			this.addEventListener('focusout', this.handleResume);

			this.buildDots();
			this.startAutoplay();
		}

		disconnectedCallback() {
			this.stopAutoplay();

			if (this.scrollFrame) {
				window.cancelAnimationFrame(this.scrollFrame);
				this.scrollFrame = null;
			}

			if (this.prevButton && this.handlePrev) this.prevButton.removeEventListener('click', this.handlePrev);

			if (this.nextButton && this.handleNext) this.nextButton.removeEventListener('click', this.handleNext);

			if (this.dotsHolder && this.handleDotClick) {
				this.dotsHolder.removeEventListener('click', this.handleDotClick);
			}

			if (this.track && this.handleScroll) this.track.removeEventListener('scroll', this.handleScroll);

			if (this.handleResize) window.removeEventListener('resize', this.handleResize);

			this.removeEventListener('mouseenter', this.handlePause);
			this.removeEventListener('mouseleave', this.handleResume);
			this.removeEventListener('focusin', this.handlePause);
			this.removeEventListener('focusout', this.handleResume);
		}

		get slideCount() {
			const width = this.track.clientWidth;

			if (width === 0) return 1;

			return Math.max(1, Math.round(this.track.scrollWidth / width));
		}

		buildDots() {
			if (!this.dotsHolder) return;

			const total = this.slideCount;
			const template = this.dotsHolder.dataset.dotLabel || '%d';

			this.dotsHolder.textContent = '';

			if (total < 2) {
				this.dotsHolder.hidden = true;
				return;
			}

			this.dotsHolder.hidden = false;

			for (let index = 0; index < total; index += 1) {
				const dot = document.createElement('button');

				dot.type = 'button';
				dot.className = 'nodus-home-hero__dot';
				dot.dataset.index = String(index);
				dot.setAttribute('aria-label', template.replace('%d', String(index + 1)));
				this.dotsHolder.appendChild(dot);
			}

			this.syncDots();
		}

		syncDots() {
			if (!this.dotsHolder) return;

			for (const dot of this.dotsHolder.children) {
				const isActive = Number(dot.dataset.index) === this.index;

				dot.classList.toggle('is-active', isActive);
				dot.setAttribute('aria-current', isActive ? 'true' : 'false');
			}
		}

		goTo(index, restart) {
			const total = this.slideCount;

			this.index = ((index % total) + total) % total;
			this.track.scrollTo({
				left: this.index * this.track.clientWidth,
				behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
			});
			this.syncDots();

			if (restart) this.startAutoplay();
		}

		onDotClick(event) {
			const dot = event.target.closest('[data-index]');

			if (dot) this.goTo(Number(dot.dataset.index), true);
		}

		onScroll() {
			if (this.scrollFrame) return;

			this.scrollFrame = window.requestAnimationFrame(() => {
				this.scrollFrame = null;

				const width = this.track.clientWidth;

				if (width === 0) return;

				const index = Math.round(this.track.scrollLeft / width);

				if (index !== this.index) {
					this.index = index;
					this.syncDots();
				}
			});
		}

		onResize() {
			this.buildDots();
			this.goTo(this.index, false);
		}

		startAutoplay() {
			this.stopAutoplay();

			if (this.dataset.autoplay !== 'true' || this.reducedMotion.matches) return;

			if (this.slideCount < 2) return;

			const delay = (Number(this.dataset.autoplayDelay) || 7) * 1000;

			this.timer = window.setInterval(() => this.goTo(this.index + 1, false), delay);
		}

		stopAutoplay() {
			if (this.timer) {
				window.clearInterval(this.timer);
				this.timer = null;
			}
		}
	}

	customElements.define('nodus-home-hero', NodusHomeHero);
}
